/**
 * Nova Test Harness — two named processes, run separately:
 *   - Kaizen Trip Intake: a ride offer lands -> Nova decides -> calls the TP
 *     -> assigns a driver -> advances status end to end -> invoices.
 *   - CPD Officer Call: an intake is captured -> (in parallel) an on-duty VA
 *     is called AND an autonomous dispatch ladder calls candidates one at a
 *     time -> first acceptance creates a trip + assigns a driver -> the
 *     driver is bridged live into a conference with the officer.
 *
 * Each process shows ONLY its own phase list — no more one blended list.
 * Deliberately styled unlike the rest of this (light) admin console — dark,
 * dense, monospace, unmissable TEST MODE marker — so it is never mistaken
 * for a live dispatch screen.
 *
 * Backend: /app/backend/test_harness.py (Kaizen engine + shared /runs* API)
 *          /app/backend/test_harness_cpd.py (CPD engine)
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { apiClient } from "../../lib/api";
import {
  Flask, PhoneOutgoing, CheckCircle, XCircle, CircleDashed, CircleNotch,
  ArrowsLeftRight, Warning, Copy, Car, Headset,
} from "@phosphor-icons/react";

const T = {
  bg: "#0b1220", stage: "#131c2b", card: "#0f1826", ink: "#f1f6fc",
  inkDim: "#b3c4da", inkMute: "#8a9db6", edge: "rgba(148,163,184,0.16)",
  hi: "#34d399", md: "#f59e0b", lo: "#ef4444", brand: "#7fd8b7",
};

const PROCESSES = [
  { id: "kaizen", label: "Kaizen Trip Intake", icon: Car },
  { id: "cpd_call", label: "CPD Officer Call", icon: Headset },
];

const PHASE_LABELS = {
  // Kaizen Trip Intake
  parse: "Parse offer", decide: "Decide + narrate", dispatch_call: "Call the TP",
  assign_driver: "Assign driver", beacon_link: "Read driver link aloud",
  advance_status: "Advance trip status", complete: "Complete trip", invoice: "Issue invoice",
  // CPD Officer Call
  intake_capture: "Capture intake", va_escalation_call: "Call on-duty VA",
  dispatch_ladder_call: "Dispatch ladder — call candidates", officer_bridge: "Bridge officer into conference",
  // Shared tools
  inbound_status_line: "Inbound status-line call", three_way_bridge: "Three-way bridge (live human pulled in)",
};

const STATUS_ICON = { pending: CircleDashed, running: CircleNotch, pass: CheckCircle, fail: XCircle };
const STATUS_COLOR = { pending: T.inkMute, running: T.md, pass: T.hi, fail: T.lo };

const SCENARIO_OPTIONS = [
  { id: "happy_path", label: "Happy path — accepts" },
  { id: "decline", label: "Declines" },
  { id: "no_answer", label: "Doesn't answer (timeout)" },
];

function ScenarioControls({ scenario, onScenario, simulate, onSimulate, idPrefix }) {
  return (
    <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
      <select value={scenario} onChange={e => onScenario(e.target.value)} data-testid={`${idPrefix}-scenario-select`}
        style={{ flex: 1, padding: 6, borderRadius: 6, background: T.stage, color: T.ink, border: `1px solid ${T.edge}`, fontSize: 11.5 }}>
        {SCENARIO_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, color: T.inkMute, whiteSpace: "nowrap" }}>
        <input type="checkbox" checked={simulate} onChange={e => onSimulate(e.target.checked)} data-testid={`${idPrefix}-simulate-check`} />
        auto-sim
      </label>
    </div>
  );
}

function PhaseRow({ phase }) {
  const [open, setOpen] = useState(false);
  const Icon = STATUS_ICON[phase.status] || CircleDashed;
  const color = STATUS_COLOR[phase.status] || T.inkMute;
  return (
    <div style={{ borderBottom: `1px solid ${T.edge}` }} data-testid={`th-phase-${phase.name}`}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 4px",
          background: "transparent", border: "none", cursor: "pointer", textAlign: "left",
        }}
        data-testid={`th-phase-toggle-${phase.name}`}
      >
        <Icon size={18} color={color} weight={phase.status === "pending" ? "regular" : "fill"}
              className={phase.status === "running" ? "th-spin" : ""} />
        <span style={{ fontFamily: "ui-monospace,monospace", fontSize: 13, color: T.ink, flex: 1 }}>
          {PHASE_LABELS[phase.name] || phase.name}
        </span>
        <span style={{ fontSize: 11, color, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          {phase.status}
        </span>
      </button>
      {open && (
        <div style={{ padding: "0 4px 12px 32px", fontSize: 12.5, color: T.inkDim }} data-testid={`th-phase-detail-${phase.name}`}>
          {phase.narration && <div style={{ marginBottom: 8, lineHeight: 1.5 }}>{phase.narration}</div>}
          {phase.error && (
            <div style={{ color: T.lo, marginBottom: 8, display: "flex", gap: 6, alignItems: "flex-start" }}>
              <Warning size={14} style={{ marginTop: 2, flexShrink: 0 }} /> {phase.error}
            </div>
          )}
          {(phase.facts_checked || []).length > 0 && (
            <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }} data-testid={`th-facts-${phase.name}`}>
              <thead>
                <tr style={{ color: T.inkMute }}>
                  <td style={{ padding: "2px 6px 2px 0" }}>fact</td>
                  <td style={{ padding: "2px 6px" }}>stated</td>
                  <td style={{ padding: "2px 6px" }}>actual</td>
                  <td style={{ padding: "2px 0" }}>match</td>
                </tr>
              </thead>
              <tbody>
                {phase.facts_checked.map((f, i) => (
                  <tr key={i}>
                    <td style={{ padding: "2px 6px 2px 0", color: T.inkDim }}>{f.label}</td>
                    <td style={{ padding: "2px 6px" }}>{String(f.stated)}</td>
                    <td style={{ padding: "2px 6px" }}>{String(f.actual)}</td>
                    <td style={{ padding: "2px 0", color: f.match ? T.hi : T.lo }}>{f.match ? "✓" : "✗"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {phase.started_at && phase.ended_at && (
            <div style={{ marginTop: 6, color: T.inkMute, fontSize: 11 }}>
              {((new Date(phase.ended_at) - new Date(phase.started_at)) / 1000).toFixed(1)}s
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TestHarnessPanel() {
  const [activeProcess, setActiveProcess] = useState("kaizen");
  const [seedTrips, setSeedTrips] = useState([]);
  const [seedTripId, setSeedTripId] = useState("");
  const [overridePhone, setOverridePhone] = useState("");
  const [kaizenScenario, setKaizenScenario] = useState("happy_path");
  const [kaizenSimulate, setKaizenSimulate] = useState(false);
  const [vaPhone, setVaPhone] = useState("");
  const [officerPhone, setOfficerPhone] = useState("");
  const [candidates, setCandidates] = useState([{ phone: "", scenario: "happy_path", simulate: false }]);
  const [officerNotifications, setOfficerNotifications] = useState(false);
  const [officerEmail, setOfficerEmail] = useState("");
  const [loadLabel, setLoadLabel] = useState("");
  const [starting, setStarting] = useState(false);
  const [runs, setRuns] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [run, setRun] = useState(null);
  const [compareIds, setCompareIds] = useState([]);
  const [compareResult, setCompareResult] = useState(null);
  const [bridgePhone, setBridgePhone] = useState("");
  const [bridging, setBridging] = useState(false);
  const [bridgeMsg, setBridgeMsg] = useState(null);
  const pollRef = useRef(null);
  const TERMINAL = ["passed", "failed", "stopped", "completed_as_expected"];

  const updateCandidate = (i, patch) => setCandidates(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));
  const addCandidate = () => setCandidates(prev => prev.length < 3 ? [...prev, { phone: "", scenario: "happy_path", simulate: false }] : prev);
  const removeCandidate = (i) => setCandidates(prev => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const applyKaizenPreset = (preset) => {
    if (preset === "happy") { setKaizenScenario("happy_path"); setKaizenSimulate(false); }
    else if (preset === "decline") { setKaizenScenario("decline"); setKaizenSimulate(true); }
    else if (preset === "no_answer") { setKaizenScenario("no_answer"); setKaizenSimulate(true); }
  };
  const applyCpdPreset = (preset) => {
    if (preset === "happy") {
      setCandidates(prev => prev.map(c => ({ ...c, scenario: "happy_path", simulate: false })));
    } else if (preset === "escalation") {
      setCandidates(prev => {
        const filled = prev.length >= 2 ? prev : [...prev, { phone: "", scenario: "happy_path", simulate: false }];
        return filled.map((c, i) => i === filled.length - 1
          ? { ...c, scenario: "happy_path", simulate: true }
          : { ...c, scenario: "decline", simulate: true });
      });
    }
  };

  const loadSeedTrips = useCallback(() => {
    apiClient.get("/floor/test-harness/kaizen/seed-trips").then(r => setSeedTrips(r.data?.trips || [])).catch(() => {});
  }, []);
  const loadRuns = useCallback(() => {
    apiClient.get("/floor/test-harness/runs").then(r => setRuns(r.data?.runs || [])).catch(() => {});
  }, []);

  useEffect(() => { loadSeedTrips(); loadRuns(); }, [loadSeedTrips, loadRuns]);

  useEffect(() => {
    setSelectedId(null);
    setRun(null);
    setCompareIds([]);
    setCompareResult(null);
  }, [activeProcess]);

  useEffect(() => {
    if (!selectedId) return;
    setBridgeMsg(null);
    setBridgePhone("");
    const poll = () => apiClient.get(`/floor/test-harness/runs/${selectedId}`).then(r => {
      setRun(r.data);
      if (TERMINAL.includes(r.data?.status)) {
        clearInterval(pollRef.current);
        loadRuns();
      }
    }).catch(() => {});
    poll();
    pollRef.current = setInterval(poll, 2500);
    return () => clearInterval(pollRef.current);
  }, [selectedId, loadRuns]);

  const visibleRuns = runs.filter(r => (r.process || "kaizen") === activeProcess);

  const canStart = activeProcess === "kaizen"
    ? !!overridePhone.trim()
    : !!(vaPhone.trim() && officerPhone.trim() && candidates.every(c => c.phone.trim()) && candidates.length > 0);

  const startRun = async () => {
    if (!canStart) return;
    setStarting(true);
    try {
      const body = activeProcess === "kaizen"
        ? { process: "kaizen", seed_trip_id: seedTripId || null, override_phone: overridePhone.trim(),
            scenario: kaizenScenario, simulate: kaizenSimulate,
            concurrent_load_label: loadLabel.trim() || null }
        : { process: "cpd_call", va_phone: vaPhone.trim(), officer_phone: officerPhone.trim(),
            dispatch_candidates: candidates.map(c => ({ phone: c.phone.trim(), scenario: c.scenario, simulate: c.simulate })),
            officer_notifications: officerNotifications, officer_email: officerNotifications ? officerEmail.trim() : null,
            concurrent_load_label: loadLabel.trim() || null };
      const { data } = await apiClient.post("/floor/test-harness/runs", body);
      setSelectedId(data.run_id);
      loadRuns();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to start run");
    } finally {
      setStarting(false);
    }
  };

  const stopRun = async () => {
    if (!selectedId) return;
    await apiClient.post(`/floor/test-harness/runs/${selectedId}/stop`).catch(() => {});
  };

  const bridgeHuman = async () => {
    if (!selectedId || !bridgePhone.trim()) return;
    setBridging(true);
    setBridgeMsg(null);
    try {
      const { data } = await apiClient.post(`/floor/test-harness/runs/${selectedId}/bridge-human`, { phone: bridgePhone.trim() });
      setBridgeMsg(data.ok ? "Bridged in — they'll hear the live call now." : "Bridge attempt failed — see phase detail below.");
    } catch (e) {
      setBridgeMsg(e?.response?.data?.detail || "Failed to bridge human into the call");
    } finally {
      setBridging(false);
    }
  };

  const runCompare = async () => {
    if (compareIds.length !== 2) return;
    const { data } = await apiClient.get(`/floor/test-harness/runs/compare?a=${compareIds[0]}&b=${compareIds[1]}`);
    setCompareResult(data);
  };

  const overallColor = run ? (run.status === "passed" ? T.hi : run.status === "failed" ? T.lo
    : run.status === "stopped" ? T.inkMute : run.status === "completed_as_expected" ? T.brand : T.md) : T.inkMute;
  const bridgeAvailable = run && run.active_call_sid && !TERMINAL.includes(run.status);

  return (
    <div style={{ background: T.bg, borderRadius: 16, padding: 20, color: T.ink, minHeight: 600 }} data-testid="test-harness-panel">
      {/* Persistent, unmissable test-mode marker */}
      <div
        style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 16px", borderRadius: 10,
          background: "repeating-linear-gradient(45deg, rgba(245,158,11,0.16) 0px, rgba(245,158,11,0.16) 10px, rgba(245,158,11,0.05) 10px, rgba(245,158,11,0.05) 20px)",
          border: `1px solid ${T.md}`, marginBottom: 18,
        }}
        data-testid="test-harness-mode-banner"
      >
        <Flask size={20} color={T.md} weight="fill" />
        <span style={{ fontFamily: "ui-monospace,monospace", fontWeight: 700, letterSpacing: "0.08em", color: T.md, fontSize: 13 }}>
          TEST MODE — NOTHING HERE TOUCHES A REAL DRIVER, TP, VA, OR BROKER
        </span>
      </div>

      {/* Process switch */}
      <div style={{ display: "flex", gap: 8, marginBottom: 18 }} data-testid="th-process-switch">
        {PROCESSES.map(p => {
          const Icon = p.icon;
          const activeP = activeProcess === p.id;
          return (
            <button key={p.id} onClick={() => setActiveProcess(p.id)} data-testid={`th-process-tab-${p.id}`}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                padding: "12px 0", borderRadius: 10, cursor: "pointer", fontSize: 13.5, fontWeight: 700,
                background: activeP ? T.brand : T.card, color: activeP ? "#04231a" : T.inkDim,
                border: `1px solid ${activeP ? T.brand : T.edge}`,
              }}>
              <Icon size={16} weight={activeP ? "fill" : "regular"} /> {p.label}
            </button>
          );
        })}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 20 }}>
        {/* Left: start form + run list */}
        <div>
          <div style={{ background: T.card, border: `1px solid ${T.edge}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
            <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 11, letterSpacing: "0.15em", color: T.inkMute, marginBottom: 10 }}>
              START A RUN — {activeProcess === "kaizen" ? "KAIZEN TRIP INTAKE" : "CPD OFFICER CALL"}
            </div>

            {activeProcess === "kaizen" ? (
              <>
                <select value={seedTripId} onChange={e => setSeedTripId(e.target.value)} data-testid="th-seed-trip-select"
                  style={{ width: "100%", padding: 8, borderRadius: 8, background: T.stage, color: T.ink, border: `1px solid ${T.edge}`, marginBottom: 10, fontSize: 12.5 }}>
                  <option value="">No seed — fully synthetic rider/address</option>
                  {seedTrips.map(t => (
                    <option key={t.trip_id} value={t.trip_id}>
                      {t.rider_name || "Rider"} · {(t.pickup_address || "").slice(0, 30)} · {t.status}
                    </option>
                  ))}
                </select>
                <input value={overridePhone} onChange={e => setOverridePhone(e.target.value)} placeholder="Override phone (stands in for TP + driver) — e.g. +13125551234"
                  data-testid="th-override-phone-input"
                  style={{ width: "100%", padding: 8, borderRadius: 8, background: T.stage, color: T.ink, border: `1px solid ${T.edge}`, marginBottom: 10, fontSize: 12.5 }} />
                <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                  <button onClick={() => applyKaizenPreset("happy")} data-testid="th-preset-happy"
                    style={{ flex: 1, padding: "5px 0", borderRadius: 6, fontSize: 10.5, cursor: "pointer",
                      background: "transparent", border: `1px solid ${T.edge}`, color: T.inkDim }}>Happy path</button>
                  <button onClick={() => applyKaizenPreset("decline")} data-testid="th-preset-decline"
                    style={{ flex: 1, padding: "5px 0", borderRadius: 6, fontSize: 10.5, cursor: "pointer",
                      background: "transparent", border: `1px solid ${T.edge}`, color: T.inkDim }}>TP declines</button>
                  <button onClick={() => applyKaizenPreset("no_answer")} data-testid="th-preset-no-answer"
                    style={{ flex: 1, padding: "5px 0", borderRadius: 6, fontSize: 10.5, cursor: "pointer",
                      background: "transparent", border: `1px solid ${T.edge}`, color: T.inkDim }}>No answer</button>
                </div>
                <ScenarioControls scenario={kaizenScenario} onScenario={setKaizenScenario}
                  simulate={kaizenSimulate} onSimulate={setKaizenSimulate} idPrefix="th-kaizen" />
                <div style={{ fontSize: 11, color: T.inkMute, marginBottom: 10, lineHeight: 1.4 }}>
                  Seeding pulls real rider/address/mobility details from a completed/cancelled trip into a
                  freshly-generated offer. "auto-sim" skips the real call and injects the expected outcome
                  instantly — a declined/no-answer scenario then ends "completed as expected", not a failure.
                </div>
              </>
            ) : (
              <>
                <input value={officerPhone} onChange={e => setOfficerPhone(e.target.value)} placeholder="Officer phone — e.g. +13125551234"
                  data-testid="th-officer-phone-input"
                  style={{ width: "100%", padding: 8, borderRadius: 8, background: T.stage, color: T.ink, border: `1px solid ${T.edge}`, marginBottom: 10, fontSize: 12.5 }} />
                <input value={vaPhone} onChange={e => setVaPhone(e.target.value)} placeholder="On-duty VA phone — e.g. +13125551234"
                  data-testid="th-va-phone-input"
                  style={{ width: "100%", padding: 8, borderRadius: 8, background: T.stage, color: T.ink, border: `1px solid ${T.edge}`, marginBottom: 10, fontSize: 12.5 }} />

                <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                  <button onClick={() => applyCpdPreset("happy")} data-testid="th-cpd-preset-happy"
                    style={{ flex: 1, padding: "5px 0", borderRadius: 6, fontSize: 10.5, cursor: "pointer",
                      background: "transparent", border: `1px solid ${T.edge}`, color: T.inkDim }}>Happy path</button>
                  <button onClick={() => applyCpdPreset("escalation")} data-testid="th-cpd-preset-escalation"
                    style={{ flex: 1, padding: "5px 0", borderRadius: 6, fontSize: 10.5, cursor: "pointer",
                      background: "transparent", border: `1px solid ${T.edge}`, color: T.inkDim }}>Multi-attempt escalation</button>
                </div>

                <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 10.5, letterSpacing: "0.1em", color: T.inkMute, marginBottom: 6 }}>
                  DISPATCH LADDER — up to 3 candidates, tried in order
                </div>
                {candidates.map((c, i) => (
                  <div key={i} style={{ background: T.stage, border: `1px solid ${T.edge}`, borderRadius: 8, padding: 8, marginBottom: 8 }}
                       data-testid={`th-candidate-row-${i}`}>
                    <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                      <input value={c.phone} onChange={e => updateCandidate(i, { phone: e.target.value })}
                        placeholder={`Candidate ${i + 1} phone`} data-testid={`th-candidate-phone-${i}`}
                        style={{ flex: 1, padding: 6, borderRadius: 6, background: T.card, color: T.ink, border: `1px solid ${T.edge}`, fontSize: 12 }} />
                      {candidates.length > 1 && (
                        <button onClick={() => removeCandidate(i)} data-testid={`th-candidate-remove-${i}`}
                          style={{ padding: "0 8px", borderRadius: 6, border: `1px solid ${T.lo}`, background: "transparent", color: T.lo, cursor: "pointer", fontSize: 12 }}>×</button>
                      )}
                    </div>
                    <ScenarioControls scenario={c.scenario} onScenario={v => updateCandidate(i, { scenario: v })}
                      simulate={c.simulate} onSimulate={v => updateCandidate(i, { simulate: v })} idPrefix={`th-candidate-${i}`} />
                  </div>
                ))}
                {candidates.length < 3 && (
                  <button onClick={addCandidate} data-testid="th-candidate-add"
                    style={{ width: "100%", padding: "6px 0", borderRadius: 8, cursor: "pointer", marginBottom: 10,
                      background: "transparent", border: `1px dashed ${T.edge}`, color: T.inkMute, fontSize: 11.5 }}>
                    + add ladder candidate
                  </button>
                )}

                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: T.inkDim, marginBottom: 6 }}>
                  <input type="checkbox" checked={officerNotifications} onChange={e => setOfficerNotifications(e.target.checked)}
                    data-testid="th-officer-notifications-check" />
                  Officer notifications (push/email as the trip progresses) — OFF by default
                </label>
                {officerNotifications && (
                  <input value={officerEmail} onChange={e => setOfficerEmail(e.target.value)} placeholder="Officer email to notify (test address)"
                    data-testid="th-officer-email-input"
                    style={{ width: "100%", padding: 8, borderRadius: 8, background: T.stage, color: T.ink, border: `1px solid ${T.edge}`, marginBottom: 8, fontSize: 12.5 }} />
                )}
                <div style={{ fontSize: 11, color: T.inkMute, marginBottom: 10, lineHeight: 1.4 }}>
                  All phones are operator-controlled stand-ins — never the real on-duty roster, real
                  Preferred Providers, or real drivers. "auto-sim" per candidate skips the real call —
                  configure candidates to decline/not-answer to rehearse the escalation ladder on demand.
                </div>
              </>
            )}

            <input value={loadLabel} onChange={e => setLoadLabel(e.target.value)} placeholder="Load label (optional) — e.g. solo / busy-floor"
              data-testid="th-load-label-input"
              style={{ width: "100%", padding: 8, borderRadius: 8, background: T.stage, color: T.ink, border: `1px solid ${T.edge}`, marginBottom: 12, fontSize: 12.5 }} />
            <button onClick={startRun} disabled={starting || !canStart}
              data-testid="th-start-run-btn"
              style={{ width: "100%", padding: "10px 0", borderRadius: 8, border: "none", cursor: "pointer",
                background: T.md, color: "#1a1000", fontWeight: 700, fontSize: 13,
                opacity: (starting || !canStart) ? 0.5 : 1 }}>
              {starting ? "Starting…" : "Start unattended run"}
            </button>
            <div style={{ fontSize: 11, color: T.inkMute, marginTop: 8, lineHeight: 1.4 }}>
              <PhoneOutgoing size={12} style={{ verticalAlign: -1 }} />{" "}
              {activeProcess === "kaizen"
                ? "Answer and press 1 to accept when the TP call rings; to check status later, call the main dispatch line from this same number and enter the run's voice code."
                : "Answer and press 1 on the VA and dispatch-candidate calls as prompted. Once a driver is assigned, the officer phone gets bridged in live."}
            </div>
          </div>

          <div style={{ background: T.card, border: `1px solid ${T.edge}`, borderRadius: 12, padding: 16 }}>
            <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 11, letterSpacing: "0.15em", color: T.inkMute, marginBottom: 10 }}>
              RECENT RUNS — {activeProcess === "kaizen" ? "KAIZEN" : "CPD"}
            </div>
            {visibleRuns.length === 0 && <div style={{ fontSize: 12, color: T.inkMute }}>No runs yet.</div>}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {visibleRuns.map(r => (
                <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <input type="checkbox" checked={compareIds.includes(r.id)} data-testid={`th-compare-check-${r.id}`}
                    onChange={e => setCompareIds(prev => e.target.checked
                      ? [...prev, r.id].slice(-2) : prev.filter(x => x !== r.id))} />
                  <button onClick={() => setSelectedId(r.id)} data-testid={`th-run-row-${r.id}`}
                    style={{
                      flex: 1, textAlign: "left", padding: "8px 10px", borderRadius: 8, cursor: "pointer",
                      background: selectedId === r.id ? "rgba(127,216,183,0.12)" : "transparent",
                      border: `1px solid ${selectedId === r.id ? T.brand : T.edge}`, color: T.ink, fontSize: 12,
                    }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>{r.process === "cpd_call" ? "CPD Officer Call" : "Kaizen Trip Intake"}</span>
                      <span style={{ color: r.status === "completed_as_expected" ? T.brand : (STATUS_COLOR[r.status] || T.inkMute), textTransform: "uppercase", fontSize: 10.5 }}>
                        {r.status.replace(/_/g, " ")}
                      </span>
                    </div>
                    <div style={{ color: T.inkMute, fontSize: 10.5 }}>
                      {r.trip_id || r.case_number || "—"} · {new Date(r.created_at).toLocaleTimeString()}
                    </div>
                  </button>
                </div>
              ))}
            </div>
            {compareIds.length === 2 && (
              <button onClick={runCompare} data-testid="th-compare-btn"
                style={{ width: "100%", marginTop: 10, padding: "8px 0", borderRadius: 8, cursor: "pointer",
                  background: "transparent", border: `1px solid ${T.brand}`, color: T.brand, fontSize: 12 }}>
                <ArrowsLeftRight size={13} style={{ verticalAlign: -2 }} /> Compare timing
              </button>
            )}
          </div>
        </div>

        {/* Right: selected run detail */}
        <div style={{ background: T.card, border: `1px solid ${T.edge}`, borderRadius: 12, padding: 20 }} data-testid="th-run-detail">
          {!run && <div style={{ color: T.inkMute, fontSize: 13 }}>Start a run, or select one from the list, to see its phase timeline here.</div>}
          {run && (
            <>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                <div>
                  <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 11, color: T.inkMute, letterSpacing: "0.1em" }}>
                    RUN {run.id.slice(0, 8)} · {run.process === "cpd_call" ? "CPD OFFICER CALL" : "KAIZEN TRIP INTAKE"}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: overallColor, marginTop: 2 }} data-testid="th-run-status">
                    {run.status.toUpperCase().replace(/_/g, " ")}
                  </div>
                  {run.failure_reason && <div style={{ color: T.lo, fontSize: 12, marginTop: 4 }}>{run.failure_reason}</div>}
                </div>
                <div style={{ textAlign: "right" }}>
                  {run.trip_id && (
                    <div style={{ fontSize: 12, color: T.inkDim, fontFamily: "ui-monospace,monospace" }} data-testid="th-run-trip-id">
                      {run.trip_id}
                    </div>
                  )}
                  {run.case_number && (
                    <div style={{ fontSize: 11, color: T.inkMute, marginTop: 2 }} data-testid="th-run-case-number">
                      case {run.case_number}
                    </div>
                  )}
                  {run.voice_lookup_code && (
                    <div style={{ fontSize: 11, color: T.inkMute, marginTop: 2 }}>
                      voice code: <b style={{ color: T.brand }} data-testid="th-voice-code">{run.voice_lookup_code}</b>
                      <Copy size={11} style={{ marginLeft: 4, cursor: "pointer", verticalAlign: -1 }}
                            onClick={() => navigator.clipboard?.writeText(run.voice_lookup_code)} />
                    </div>
                  )}
                  {!TERMINAL.includes(run.status) && (
                    <button onClick={stopRun} data-testid="th-stop-run-btn"
                      style={{ marginTop: 8, padding: "5px 12px", borderRadius: 6, border: `1px solid ${T.lo}`,
                        background: "transparent", color: T.lo, cursor: "pointer", fontSize: 11 }}>
                      Stop run
                    </button>
                  )}
                </div>
              </div>

              {bridgeAvailable && (
                <div style={{ background: T.stage, border: `1px solid ${T.brand}`, borderRadius: 10, padding: 12, marginBottom: 16 }}
                     data-testid="th-bridge-panel">
                  <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 11, letterSpacing: "0.1em", color: T.brand, marginBottom: 8 }}>
                    THREE-WAY BRIDGE — pull a live human into this in-progress call
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <input value={bridgePhone} onChange={e => setBridgePhone(e.target.value)} placeholder="Phone to bridge in — e.g. +13125551234"
                      data-testid="th-bridge-phone-input"
                      style={{ flex: 1, padding: 8, borderRadius: 8, background: T.card, color: T.ink, border: `1px solid ${T.edge}`, fontSize: 12.5 }} />
                    <button onClick={bridgeHuman} disabled={bridging || !bridgePhone.trim()} data-testid="th-bridge-btn"
                      style={{ padding: "8px 14px", borderRadius: 8, border: "none", cursor: "pointer",
                        background: T.brand, color: "#04231a", fontWeight: 700, fontSize: 12.5,
                        opacity: (bridging || !bridgePhone.trim()) ? 0.5 : 1 }}>
                      {bridging ? "Bridging…" : "Bridge in"}
                    </button>
                  </div>
                  {bridgeMsg && <div style={{ marginTop: 8, fontSize: 11.5, color: T.inkDim }} data-testid="th-bridge-msg">{bridgeMsg}</div>}
                  <div style={{ fontSize: 11, color: T.inkMute, marginTop: 8, lineHeight: 1.4 }}>
                    Redirects the SAME live call — the person on the ringing/answered leg hears the new
                    party join mid-call, exactly like escalating a real dispatch call. Works from either
                    process's run detail.
                  </div>
                </div>
              )}

              <div style={{ borderTop: `1px solid ${T.edge}` }}>
                {(run.phases || []).map(p => <PhaseRow key={p.name} phase={p} />)}
              </div>

              {run.total_duration_seconds != null && (
                <div style={{ marginTop: 12, fontSize: 11.5, color: T.inkMute }}>
                  Total duration: {run.total_duration_seconds}s
                  {run.concurrent_load_label && <> · load label: {run.concurrent_load_label}</>}
                </div>
              )}
            </>
          )}

          {compareResult && (
            <div style={{ marginTop: 20, borderTop: `1px solid ${T.edge}`, paddingTop: 16 }} data-testid="th-compare-result">
              <div style={{ fontFamily: "ui-monospace,monospace", fontSize: 11, color: T.inkMute, marginBottom: 8 }}>
                {compareResult.a.label.toUpperCase()} ({compareResult.a.total_duration_seconds}s) vs{" "}
                {compareResult.b.label.toUpperCase()} ({compareResult.b.total_duration_seconds}s)
              </div>
              <table style={{ width: "100%", fontSize: 12 }}>
                <tbody>
                  {compareResult.phase_deltas.map(d => (
                    <tr key={d.phase}>
                      <td style={{ padding: "3px 0", color: T.inkDim }}>{PHASE_LABELS[d.phase] || d.phase}</td>
                      <td style={{ padding: "3px 8px", textAlign: "right" }}>{d.a_seconds ?? "—"}s</td>
                      <td style={{ padding: "3px 8px", textAlign: "right" }}>{d.b_seconds ?? "—"}s</td>
                      <td style={{ padding: "3px 0", textAlign: "right", color: d.delta_seconds > 0 ? T.md : T.hi }}>
                        {d.delta_seconds != null ? `${d.delta_seconds > 0 ? "+" : ""}${d.delta_seconds}s` : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <style>{`
        .th-spin { animation: th-spin 1s linear infinite; }
        @keyframes th-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
